import React, { useContext, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Tooltip,
  Button,
  Chip,
} from "@mui/material";
import {
  UploadFile,
  Visibility,
  Download as DownloadIcon,
  CalendarToday,
  Tag,
  LocationOn,
  CreditCard,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import formatDate from "../../../../utils/FormatDate";
import FormatCurrency from "../../../../utils/FormatCurrency";
import ModalTicketPayment from "../../../Cart/ModalTicketPayment";
import OrdersContext from "../../../../Context/Orders/OrdersContext";

const statusConfig = {
  0: { color: "#E65100", bg: "#FFF3E0", text: "Pendiente de pago" },
  1: { color: "#1565C0", bg: "#E3F2FD", text: "Validando pago" },
  2: { color: "#6A1B9A", bg: "#F3E5F5", text: "En preparación" },
  3: { color: "#2E7D32", bg: "#E8F5E9", text: "Enviado" },
  4: { color: "#C62828", bg: "#FFEBEE", text: "Cancelado" },
};

const Card = ({ order, index, onCollapse, active, downloadInvoice }) => {
  const { OrderInvoice, GetPdfInvoice } = useContext(OrdersContext);
  const {
    direction,
    status,
    id,
    subtotal,
    shipping_cost = 0,
    total,
    payments,
    aditional_discount = 0,
    business,
    created_at,
    payment_type,
  } = order;

  const [modalState, setModalState] = useState({
    open: false,
    order_id: null,
    total: null,
    envio: null,
  });

  const handleModal = (open, id = null, total = null, envio = null) => {
    setModalState({ open, order_id: id, total, envio });
  };

  const totalPagado =
    payments?.reduce((sum, payment) => sum + payment.amount, 0) || 0;

  const isCardPayment = ["openpay", "tarjeta"].includes(payment_type);
  
  const paymentMethodLabel = {
    openpay: "Tarjeta",
    tarjeta: "Tarjeta",
    transferencia: "Transferencia",
    mercadopago: "Mercado Pago"
  }[payment_type] || "Transferencia";

  const deliveryAddress = order.direction
    ? [
      order.direction.street,
      order.direction.between_street,
      order.direction.number_ext,
      order.direction.city,
      order.direction.state?.name,
      order.direction.municipality?.name,
      order.direction.postal_code,
    ]
      .filter(Boolean)
      .join(", ")
    : "Dirección no disponible";

  const statusInfo = statusConfig[status] || statusConfig[0];

  // Show upload button only for transfer payments where total doesn't match
  const showUploadButton =
    !isCardPayment && total + shipping_cost !== totalPagado;

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        borderRadius: 3,
        border: active === index ? "2px solid #801F56" : "1px solid #F0E0E8",
        backgroundColor: active === index ? "#FFF8FB" : "#fff",
        cursor: "pointer",
        transition: "all 0.2s ease",
        overflow: "hidden",
        "&:hover": {
          borderColor: "#D87390",
          boxShadow: "0 4px 16px rgba(128, 34, 87, 0.1)",
        },
      }}
      onClick={() => onCollapse(index)}
    >
      {/* Top bar with status */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2.5,
          py: 1.5,
          borderBottom: "1px solid #F5E8EF",
          background: "linear-gradient(135deg, #FEFAFC 0%, #FFF5F9 100%)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Tag sx={{ fontSize: 16, color: "#801F56" }} />
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: "#801F56" }}
            >
              Pedido #{id}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <CalendarToday sx={{ fontSize: 14, color: "#999" }} />
            <Typography variant="caption" sx={{ color: "#888" }}>
              {formatDate(created_at)}
            </Typography>
          </Box>
          <Chip
            icon={<CreditCard sx={{ fontSize: 14 }} />}
            label={paymentMethodLabel}
            size="small"
            sx={{
              height: 22,
              fontSize: "0.7rem",
              fontWeight: 600,
              backgroundColor: isCardPayment ? "#E3F2FD" : "#F5F5F5",
              color: isCardPayment ? "#1565C0" : "#666",
              "& .MuiChip-icon": { color: isCardPayment ? "#1565C0" : "#666" },
            }}
          />
        </Box>
        <Chip
          label={statusInfo.text}
          size="small"
          sx={{
            fontWeight: 700,
            fontSize: "0.7rem",
            height: 24,
            backgroundColor: statusInfo.bg,
            color: statusInfo.color,
            border: `1px solid ${statusInfo.color}20`,
          }}
        />
      </Box>

      {/* Content */}
      <Box sx={{ px: 2.5, py: 2 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Address */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
              <LocationOn sx={{ fontSize: 16, color: "#999", mt: 0.3 }} />
              <Typography
                variant="caption"
                sx={{ color: "#666", lineHeight: 1.4 }}
              >
                {deliveryAddress}
              </Typography>
            </Box>
          </Grid>

          {/* Price details */}
          <Grid item xs={6} sm={3} md={2}>
            <Typography
              variant="caption"
              sx={{ color: "#999", display: "block" }}
            >
              Subtotal
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: "#444" }}>
              {FormatCurrency(subtotal)}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <Typography
              variant="caption"
              sx={{ color: "#999", display: "block" }}
            >
              Envío
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: "#444" }}>
              {FormatCurrency(Number(shipping_cost))}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <Typography
              variant="caption"
              sx={{ color: "#999", display: "block" }}
            >
              Descuento
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: "#444" }}>
              {business?.discount || 0}%
              {aditional_discount > 0 && ` + ${aditional_discount}%`}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <Typography
              variant="caption"
              sx={{ color: "#999", display: "block" }}
            >
              Total
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 800, color: "#801F56", fontSize: "1rem" }}
            >
              {FormatCurrency(total + Number(shipping_cost))}
            </Typography>
          </Grid>
        </Grid>

        {/* Actions */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            mt: 2,
            pt: 1.5,
            borderTop: "1px solid #F5E8EF",
            flexWrap: "wrap",
          }}
        >
          {showUploadButton && (
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => {
                handleModal(true, id, total, shipping_cost);
                e.stopPropagation();
              }}
              startIcon={<UploadFile sx={{ fontSize: 16 }} />}
              sx={{
                borderColor: "#801F56",
                color: "#801F56",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.8rem",
                borderRadius: 2,
                "&:hover": {
                  borderColor: "#6B1A48",
                  backgroundColor: "#FFF0F5",
                },
              }}
            >
              Subir comprobante
            </Button>
          )}
          {isCardPayment && status === 0 && (
            <Link
              to={`/pago-orden/${id}`}
              style={{ textDecoration: "none" }}
              onClick={(e) => e.stopPropagation()}
            >
              <Button
                size="small"
                variant="outlined"
                startIcon={<CreditCard sx={{ fontSize: 16 }} />}
                sx={{
                  borderColor: "#1565C0",
                  color: "#1565C0",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  borderRadius: 2,
                  "&:hover": {
                    borderColor: "#0D47A1",
                    backgroundColor: "#E3F2FD",
                  },
                }}
              >
                Pagar con tarjeta
              </Button>
            </Link>
          )}
          <Link
            to={`/Detalle-venta/${id}`}
            style={{ textDecoration: "none" }}
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              size="small"
              variant="contained"
              startIcon={<Visibility sx={{ fontSize: 16 }} />}
              sx={{
                background:
                  "linear-gradient(135deg, #801F56 0%, #D87390 100%)",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.8rem",
                borderRadius: 2,
                boxShadow: "none",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #6B1A48 0%, #A93B7A 100%)",
                  boxShadow: "0 2px 8px rgba(128, 34, 87, 0.3)",
                },
              }}
            >
              Ver detalle
            </Button>
          </Link>
          {order.invoice === "si" &&
            order.invoiced_id === null &&
            order.status === 3 && (
              <Button
                size="small"
                onClick={(e) => {
                  OrderInvoice(order.id);
                  e.stopPropagation();
                }}
                variant="contained"
                color="success"
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  borderRadius: 2,
                  boxShadow: "none",
                }}
              >
                Facturar
              </Button>
            )}
          {downloadInvoice &&
            order.invoice === "si" &&
            order.invoiced_id !== null &&
            order.status === 3 && (
              <Button
                size="small"
                onClick={(e) => {
                  GetPdfInvoice(order.id);
                  e.stopPropagation();
                }}
                variant="contained"
                color="success"
                startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  borderRadius: 2,
                  boxShadow: "none",
                }}
              >
                Descargar Factura
              </Button>
            )}
        </Box>
      </Box>
      {modalState.open && (
        <ModalTicketPayment
          {...modalState}
          handleClose={() => handleModal(false)}
          total_pagado={totalPagado}
        />
      )}
    </Paper>
  );
};

export default Card;
